import { createFeature, createReducer, on } from '@ngrx/store';
import {
  ConnectedDevice,
  WsEventType,
} from '@app/core/realtime/realtime.types';
import * as RealtimeActions from '@app/shared/store/realtime/realtime.actions';
import type { OnlineSession } from '../models/user-activity.model';
import * as UserActivityActions from './user-activity.actions';

export interface UserActivityState {
  /** Online sessions keyed by session id. */
  sessions: Record<string, OnlineSession>;
  /** True once a snapshot arrived; live events are applied only on top of one. */
  loaded: boolean;
  loading: boolean;
  error: string | null;
  /** Successful sign-ins announced (LOGIN_RECORDED) since the history tab last loaded. */
  newLogins: number;
}

export const initialUserActivityState: UserActivityState = {
  sessions: {},
  loaded: false,
  loading: false,
  error: null,
  newLogins: 0,
};

function sessionFromDevice(device: ConnectedDevice): OnlineSession {
  return {
    sessionId: device.sessionId,
    userId: device.userId,
    firstname: device.firstname ?? null,
    lastname: device.lastname ?? null,
    role: device.role,
    deviceType: device.deviceType,
    deviceName: device.deviceName ?? null,
    os: device.os ?? null,
    browser: device.browser ?? null,
    ipAddress: device.ipAddress ?? null,
    userAgent: device.userAgent ?? null,
    connectedAt: device.connectedAt,
    socketIds: [device.socketId],
  };
}

/** Adds a socket to its session (creating the row), idempotently. */
function withSocket(
  sessions: Record<string, OnlineSession>,
  device: ConnectedDevice
): Record<string, OnlineSession> {
  const existing = sessions[device.sessionId];
  if (!existing) {
    return { ...sessions, [device.sessionId]: sessionFromDevice(device) };
  }
  if (existing.socketIds.includes(device.socketId)) {
    return sessions;
  }
  return {
    ...sessions,
    [device.sessionId]: {
      ...existing,
      socketIds: [...existing.socketIds, device.socketId],
      connectedAt:
        device.connectedAt < existing.connectedAt
          ? device.connectedAt
          : existing.connectedAt,
    },
  };
}

/** Removes a socket wherever it is; a session with no socket left is offline. */
function withoutSocket(
  sessions: Record<string, OnlineSession>,
  socketId: string
): Record<string, OnlineSession> {
  const owner = Object.values(sessions).find(s =>
    s.socketIds.includes(socketId)
  );
  if (!owner) {
    return sessions;
  }
  const { [owner.sessionId]: _removed, ...rest } = sessions;
  const socketIds = owner.socketIds.filter(id => id !== socketId);
  return socketIds.length === 0
    ? rest
    : { ...rest, [owner.sessionId]: { ...owner, socketIds } };
}

const reducer = createReducer(
  initialUserActivityState,

  on(UserActivityActions.loadOnlineSessions, state => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(UserActivityActions.loadOnlineSessionsSuccess, (state, { sessions }) => ({
    ...state,
    loading: false,
    loaded: true,
    sessions: Object.fromEntries(sessions.map(s => [s.sessionId, s])),
  })),

  on(UserActivityActions.loadOnlineSessionsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  on(RealtimeActions.realtimeDeviceConnected, (state, { device }) =>
    state.loaded
      ? { ...state, sessions: withSocket(state.sessions, device) }
      : state
  ),

  on(RealtimeActions.realtimeDeviceDisconnected, (state, { event }) =>
    state.loaded
      ? { ...state, sessions: withoutSocket(state.sessions, event.socketId) }
      : state
  ),

  on(RealtimeActions.realtimeEventReceived, (state, { envelope }) =>
    envelope.type === WsEventType.LOGIN_RECORDED
      ? { ...state, newLogins: state.newLogins + 1 }
      : state
  ),

  on(UserActivityActions.loginHistoryReloaded, state => ({
    ...state,
    newLogins: 0,
  }))
);

export const userActivityFeature = createFeature({
  name: 'userActivity',
  reducer,
});
